import { Solution, SOLUTIONS_NL } from '@energiebespaarders/constants';
import type { AutoAdviceTopic } from './types';

export const solutionAdvantages: Record<Solution, string[]> = {
  [Solution.WallInsulation]: [
    'Een relatief goedkope maatregel met een korte terugverdientijd',
    'Verhoog je comfort, het voelt warmer aan in je woning',
    'Stijging van woningwaarde door verbetering van je energielabel',
  ],
  [Solution.FloorInsulation]: [
    'Geen koude voeten meer',
    'Stijging van woningwaarde door verbetering van je energielabel',
  ],
  [Solution.CrawlspaceInsulation]: [
    'Geen koude voeten meer',
    'Stijging van woningwaarde door verbetering van je energielabel',
  ],
  [Solution.InnerRoofInsulation]: [
    'Verhoging van je wooncomfort op je zolder of bovenverdieping',
    'Stijging van woningwaarde door verbetering van je energielabel',
  ],
  [Solution.AtticFloorInsulation]: [
    'Verhoging van je wooncomfort op je zolder of bovenverdieping',
    'Stijging van woningwaarde door verbetering van je energielabel',
  ],
  [Solution.WindowGlazing]: [
    'Verhoging van je wooncomfort door minder koude lucht',
    'Minder last van geluidsoverlast',
    'Stijging van woningwaarde door verbetering van je energielabel',
  ],
  [Solution.PvSystem]: [
    'Groene stroom die je zelf opwekt',
    '0% btw op aanschaf en installatie',
    'Stijging van woningwaarde door verbetering van je energielabel',
  ],
  [Solution.HybridHeatPump]: [
    'Tot 80% van je gasverbruik verminderen',
    'Minder last van veranderende gasprijzen',
    'Verhoog je comfort door constante temperatuur en koeling',
    '€1.950 tot €5.100 subsidie afhankelijk van het type warmtepomp',
    'Stijging van woningwaarde door verbetering van je energielabel',
  ],
  [Solution.ElectricHeatPump]: [
    'Van het gas af',
    'Geen last meer van veranderende gasprijzen',
    'Verhoog je comfort door constante temperatuur en koeling',
    '€1.950 tot €5.100 subsidie afhankelijk van het type warmtepomp',
    'Stijging van woningwaarde door verbetering van je energielabel',
  ],
  [Solution.CentralHeatingBoiler]: [
    'Verhoog je wooncomfort doordat je sneller warm water hebt',
    'Stiller dan oudere cv-ketels',
    'Slimme thermostaat standaard meegeleverd',
  ],
  [Solution.AirToAirHeatPump]: [
    'Tot 60% besparen op verwarmen vergeleken met gasverwarming',
    'Koelt en verwarmt sneller dan alle andere producten.',
    'Comfortabeler binnenklimaat door constante temperatuur en koeling',
    'Verbetert luchtkwaliteit door stofdeeltjes weg te filteren',
    'Slimme combinatie met zonnepanelen',
  ],
  [Solution.WindowFrames]: [],
  [Solution.Miscellaneous]: [],
  [Solution.Sedum]: [],
  [Solution.ChargingBox]: [],
  [Solution.UnderfloorHeating]: [],
  [Solution.HomeBattery]: [],
};

/** The amount with which to increase or decrease the custom estimate.amount */
export const estimateAmountIncrements: Record<Solution, number> = {
  [Solution.WallInsulation]: 5,
  [Solution.FloorInsulation]: 5,
  [Solution.CrawlspaceInsulation]: 5,
  [Solution.InnerRoofInsulation]: 5,
  [Solution.AtticFloorInsulation]: 5,
  [Solution.WindowGlazing]: 5,
  [Solution.WindowFrames]: 5,
  [Solution.PvSystem]: 1,
  [Solution.HybridHeatPump]: 1,
  [Solution.ElectricHeatPump]: 1,
  [Solution.AirToAirHeatPump]: 1,
  [Solution.CentralHeatingBoiler]: 1,
  [Solution.Miscellaneous]: 1,
  [Solution.Sedum]: 1,
  [Solution.ChargingBox]: 1,
  [Solution.UnderfloorHeating]: 1,
  [Solution.HomeBattery]: 3500,
};

export const tcoExampleTableRows = [
  [
    'Kosten',
    'Scenario 1: CV-ketel',
    'Scenario 2: Hybride warmtepomp',
    'Scenario 3: Elektrische warmtepomp',
  ],
  ['Eenmalige investering CV', '€2.000', '€2.000', '€0'],
  ['Eenmalige investering warmtepomp', '€0', '€8.000', '€15.000'],
  ['Gasverbruik', '€56.250', '€16.875', '€0'],
  ['Stroomverbruik', '€4.125', '€19.635', '€26.815'],
  ['Vaste kosten gasaansluiting', '€2.580', '€2.580', '€0'],
  ['Onderhoudskosten', '€1.080', '€1.620', '€2.160'],
  ['Subsidie', '€0', '-€2.700', '-€3.150'],
  ['Totaal over 15 jaar', '€66.035', '€46.010', '€40.820'],
  ['CO2-reductie op verwarmen', '0%', '50%', '100%'],
];

// Arbitrary, based on attractiveness
export const estimateSolutionSortOrder: Solution[] = [
  Solution.PvSystem,
  Solution.HybridHeatPump,
  Solution.ElectricHeatPump,
  Solution.FloorInsulation,
  Solution.WallInsulation,
  Solution.InnerRoofInsulation,
  Solution.AirToAirHeatPump,
  Solution.HomeBattery,
  Solution.WindowGlazing,
  Solution.CentralHeatingBoiler,
];

// Used to sort estimates
export const feasibilityStatusOrder = [
  'feasible',
  'feasibleWithCaveats',
  'unknown',
  'installed',
  'infeasible',
];

/**
 * Renames "Dakisolatie binnenzijde" to "Dakisolatie" and "Vloerisolatie" to "Vloer- of bodemisolatie"
 *
 */
export const selfServiceIntakeSolutionsNL: Record<Solution, string> = Object.entries(
  SOLUTIONS_NL,
).reduce(
  (acc, [solution, translation]) => ({
    ...acc,
    [solution]: translation
      .replace(' binnenzijde', '')
      .replace('loerisolatie', 'loer- of bodemisolatie'),
  }),
  {},
) as Record<Solution, string>;

export const getAdviceIntroPerTopic = (topic: AutoAdviceTopic) => {
  switch (topic) {
    case 'insulation':
      return (
        <div>
          <p>
            Wil je je huis verduurzamen? Begin dan altijd met isoleren. Zonder goede isolatie gaat
            veel warmte verloren via de muren, vloer, ramen en het dak. Dit zorgt voor een hoger
            energieverbruik en onnodige kosten. Daarom is isoleren de belangrijkste eerste stap.
          </p>

          <p>
            Goede isolatie houdt in de winter de warmte binnen, waardoor je minder hoeft te stoken
            en direct bespaart op je energierekening. In de zomer blijft je huis juist koeler. Zo
            verhoogt isolatie niet alleen de energie-efficiëntie, maar ook je wooncomfort.
          </p>

          <p>
            Isoleren verdient zichzelf vaak snel terug, vooral bij voordelige opties zoals
            spouwmuurisolatie of vloerisolatie. Er zijn ook uitgebreidere mogelijkheden, zoals
            dakisolatie, HR++ glas of buitenmuurisolatie.
          </p>

          <p>
            De overheid biedt subsidies om isoleren voordeliger te maken. Bij ieder product staat
            een inschatting van de subsidie die je er voor kunt krijgen.
          </p>
        </div>
      );
    case 'energyProduction':
      return (
        <div>
          <p>
            Heb je je huis goed geïsoleerd? Dan is de volgende stap: zelf duurzame energie opwekken.
            Zo maak je je minder afhankelijk van fossiele bronnen en verlaag je je energierekening.
            De meest gekozen manier is het plaatsen van zonnepanelen. Hiermee wek je je eigen
            elektriciteit op, gewoon op je dak.
          </p>

          <p>
            Zonnepanelen leveren stroom zodra de zon schijnt, ook op bewolkte dagen. Wat je niet
            direct verbruikt, lever je terug aan het net. Daarmee bespaar je niet alleen op je
            verbruik, maar verdien je de investering ook stap voor stap terug.
          </p>

          <p>
            Voor woningen met een warmtepomp of elektrische auto kan het aantrekkelijk zijn om extra
            panelen te plaatsen. Zo dek je ook dat verbruik duurzaam af. Wil je nóg meer controle?
            Dan kun je denken aan een thuisbatterij of energiebeheer met een slimme app.
          </p>
        </div>
      );
    case 'heating':
      return (
        <div>
          <p>
            De laatste stap is het verduurzamen van je verwarming. Traditionele cv-ketels werken op
            aardgas, wat veel CO<sub>2</sub>-uitstoot veroorzaakt. Door over te stappen op een
            duurzaam verwarmingssysteem, verminder je die uitstoot én maak je je huis
            toekomstbestendig.
          </p>

          <p>
            Een populaire keuze is de (hybride) warmtepomp, die je woning grotendeels of volledig
            verwarmt met energie uit de lucht of de bodem. Dit is vooral effectief in goed
            geïsoleerde huizen. In sommige gevallen kun je ook aansluiten op een warmtenet of kiezen
            voor infraroodpanelen als bijverwarming.
          </p>

          <p>
            Duurzaam verwarmen vraagt een investering, maar de energiebesparing is blijvend. Met de
            juiste combinatie van isolatie, opwekking en verwarming kun je in veel gevallen volledig
            van het gas af.
          </p>

          <p>
            Ook hier biedt de overheid subsidies, bijvoorbeeld via de ISDE-regeling. We vermelden
            per product welke vergoeding je kunt verwachten.
          </p>
        </div>
      );
    case 'ventilation':
    default:
      return null;
  }
};
